/**
 * Browse a Criterion's Policy Versions as a unified diff of app-current vs previous.
 *
 * Eligibility judging stays on Policy Comparison. This dialog is read-only: two
 * selectors, arrows that step the selected version, and DiffPane.
 */
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Criteria, Policy } from '../../types';
import { policyVersionOptionLabel } from './criteriaPolicySearch';
import { DiffMode, DiffPane, DiffSide } from './PolicyComparison/diff/DiffPane';
import {
  applyComparison,
  applySelected,
  canStep,
  defaultHistorySelection,
  HistorySelection,
  lineageOldestFirst,
  moveSelected,
  orderedDiffPair,
} from './policyHistorySelection';

const DEFAULT_DIFF_MODE: DiffMode = 'pierre-unified-word';

const toDiffSide = (policy: Policy): DiffSide => ({
  version: policy.version ?? 0,
  text: policy.text,
});

const PolicyHistoryDialog = ({
  open,
  criterion,
  policies,
  onClose,
}: {
  open: boolean;
  criterion: Criteria | null;
  policies: Policy[];
  onClose: () => void;
}) => {
  const criterionId = criterion?.id;
  const currentPolicyId = criterion?.policyId ?? null;
  const lineage = useMemo(
    () => (criterionId ? lineageOldestFirst(policies, criterionId) : []),
    [policies, criterionId]
  );
  const [selection, setSelection] = useState<HistorySelection | null>(() =>
    criterion
      ? defaultHistorySelection(lineageOldestFirst(policies, criterion.id), criterion.policyId)
      : null
  );
  const [diffMode, setDiffMode] = useState<DiffMode>(DEFAULT_DIFF_MODE);

  useEffect(() => {
    if (!open) return;
    setSelection(defaultHistorySelection(lineage, currentPolicyId));
    setDiffMode(DEFAULT_DIFF_MODE);
  }, [open, currentPolicyId, criterionId, lineage]);

  const selected = lineage.find(policy => policy.id === selection?.selectedId);
  const comparison =
    selection?.comparisonId != null
      ? lineage.find(policy => policy.id === selection.comparisonId)
      : undefined;
  const canGoOlder = Boolean(selection && canStep(lineage, selection.selectedId, -1));
  const canGoNewer = Boolean(selection && canStep(lineage, selection.selectedId, 1));
  const hasComparisonChoice = lineage.length > 1;
  const sameVersion = Boolean(selected && comparison && selected.id === comparison.id);
  const pair =
    selected && comparison && !sameVersion ? orderedDiffPair(selected, comparison) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        {criterion ? `Policy history — ${criterion.name}` : 'Policy history'}
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'nowrap',
            flexShrink: 0,
            mt: 1,
            mb: 2,
          }}
        >
          <Tooltip title="Older Policy Version">
            <span>
              <IconButton
                size="small"
                aria-label="Older Policy Version"
                disabled={!canGoOlder}
                onClick={() => selection && setSelection(moveSelected(lineage, selection, -1))}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={!selected}>
            <InputLabel id="policy-history-selected-label">Policy</InputLabel>
            <Select
              labelId="policy-history-selected-label"
              label="Policy"
              value={selection?.selectedId ?? ''}
              onChange={event =>
                selection && setSelection(applySelected(lineage, selection, event.target.value))
              }
            >
              {lineage.map(policy => (
                <MenuItem key={policy.id} value={policy.id}>
                  {policyVersionOptionLabel(policy, policy.id === currentPolicyId)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Newer Policy Version">
            <span>
              <IconButton
                size="small"
                aria-label="Newer Policy Version"
                disabled={!canGoNewer}
                onClick={() => selection && setSelection(moveSelected(lineage, selection, 1))}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
          <FormControl
            size="small"
            sx={{ minWidth: 220, ml: { sm: 1 } }}
            disabled={!hasComparisonChoice}
          >
            <InputLabel id="policy-history-comparison-label">Compare with</InputLabel>
            <Select
              labelId="policy-history-comparison-label"
              label="Compare with"
              value={selection?.comparisonId ?? ''}
              displayEmpty
              onChange={event =>
                selection &&
                event.target.value &&
                setSelection(applyComparison(lineage, selection, event.target.value))
              }
            >
              {selection?.comparisonId ? null : (
                <MenuItem value="" disabled>
                  No earlier version
                </MenuItem>
              )}
              {lineage.map(policy => (
                <MenuItem key={policy.id} value={policy.id}>
                  {policyVersionOptionLabel(policy, policy.id === currentPolicyId)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {pair ? (
            <DiffPane
              older={toDiffSide(pair.older)}
              newer={toDiffSide(pair.newer)}
              mode={diffMode}
              onModeChange={setDiffMode}
            />
          ) : sameVersion ? (
            <Alert severity="info">
              The selected Policy and comparison are the same version. Choose a different comparison
              to see a diff.
            </Alert>
          ) : selected ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                No earlier Policy Version to compare.
              </Alert>
              <Box sx={{ '& p': { mb: 1, mt: 0 }, fontSize: 14, lineHeight: 1.65 }}>
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{selected.text}</ReactMarkdown>
              </Box>
            </>
          ) : (
            <Typography color="text.secondary">No Policy to show.</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyHistoryDialog;
